import { useEffect, useRef } from "react";

export default function WaterCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let time = 0;
    let mouse = { x: 0.5, y: 0.5 };
    let ripples = [];

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };

    const addRipple = (x, y) => {
      ripples.push({ x, y, t: 0, maxT: 120, strength: 0.015 });
      if (ripples.length > 5) ripples.shift();
    };

    const drawWater = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      // Draw flowing water layers (back to front)
      const layers = [
        { y: 0.35, amp: 30, freq: 0.003, speed: 0.008, color1: "rgba(6, 182, 212, 0.04)", color2: "rgba(59, 130, 246, 0.03)" },
        { y: 0.45, amp: 25, freq: 0.004, speed: 0.012, color1: "rgba(59, 130, 246, 0.05)", color2: "rgba(6, 182, 212, 0.04)" },
        { y: 0.55, amp: 20, freq: 0.005, speed: 0.015, color1: "rgba(6, 182, 212, 0.06)", color2: "rgba(59, 130, 246, 0.05)" },
        { y: 0.65, amp: 18, freq: 0.006, speed: 0.01, color1: "rgba(59, 130, 246, 0.04)", color2: "rgba(6, 182, 212, 0.06)" },
        { y: 0.75, amp: 15, freq: 0.007, speed: 0.018, color1: "rgba(6, 182, 212, 0.05)", color2: "rgba(59, 130, 246, 0.04)" },
      ];

      // Caustic light patterns (like light through water)
      for (let i = 0; i < 6; i++) {
        const cx = w * (0.1 + i * 0.16 + Math.sin(time * 0.3 + i * 1.2) * 0.05);
        const cy = h * (0.3 + Math.cos(time * 0.2 + i * 0.8) * 0.15);
        const r = 80 + Math.sin(time * 0.5 + i) * 30;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, `rgba(6, 182, 212, ${0.06 + Math.sin(time * 0.4 + i) * 0.02})`);
        grad.addColorStop(0.5, `rgba(59, 130, 246, ${0.03 + Math.sin(time * 0.3 + i) * 0.01})`);
        grad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      // Draw flowing wave layers
      layers.forEach((layer) => {
        ctx.beginPath();
        const baseY = h * layer.y;
        ctx.moveTo(0, h);

        for (let x = 0; x <= w; x += 2) {
          let y = baseY;
          // Main wave
          y += Math.sin(x * layer.freq + time * layer.speed * 60) * layer.amp;
          // Secondary wave
          y += Math.sin(x * layer.freq * 2.3 + time * layer.speed * 40 + 1.5) * layer.amp * 0.4;
          // Tertiary wave
          y += Math.cos(x * layer.freq * 0.7 + time * layer.speed * 25) * layer.amp * 0.3;
          // Mouse influence
          const dx = (x / w) - mouse.x;
          const dy = (baseY / h) - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 0.3) {
            y += Math.sin(dist * 20 - time * 3) * 8 * (1 - dist / 0.3);
          }
          // Ripple influence
          ripples.forEach((rip) => {
            const rdx = (x / w) - rip.x;
            const rdy = (baseY / h) - rip.y;
            const rdist = Math.sqrt(rdx * rdx + rdy * rdy);
            const progress = rip.t / rip.maxT;
            const ripRadius = progress * 0.5;
            if (Math.abs(rdist - ripRadius) < 0.1) {
              const fade = 1 - progress;
              y += Math.sin((rdist - ripRadius) * 60) * 12 * fade * rip.strength * 60;
            }
          });
          ctx.lineTo(x, y);
        }

        ctx.lineTo(w, h);
        ctx.closePath();
        const grad = ctx.createLinearGradient(0, baseY - layer.amp, 0, h);
        grad.addColorStop(0, layer.color1);
        grad.addColorStop(1, layer.color2);
        ctx.fillStyle = grad;
        ctx.fill();
      });

      // Light reflections (horizontal streaks)
      for (let i = 0; i < 4; i++) {
        const ry = h * (0.2 + i * 0.18 + Math.sin(time * 0.15 + i * 2) * 0.05);
        const rw = w * (0.15 + Math.sin(time * 0.25 + i * 1.5) * 0.08);
        const rx = w * (0.1 + i * 0.22 + Math.sin(time * 0.1 + i) * 0.05);
        const alpha = 0.03 + Math.sin(time * 0.3 + i * 1.1) * 0.015;
        const grad = ctx.createLinearGradient(rx, ry, rx + rw, ry);
        grad.addColorStop(0, "rgba(255, 255, 255, 0)");
        grad.addColorStop(0.3, `rgba(255, 255, 255, ${alpha})`);
        grad.addColorStop(0.7, `rgba(255, 255, 255, ${alpha})`);
        grad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(rx, ry - 1, rw, 2.5);
      }

      // Update ripples
      ripples = ripples.filter((r) => {
        r.t++;
        return r.t < r.maxT;
      });

      time += 0.016;
      animId = requestAnimationFrame(drawWater);
    };

    const handleMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = (e.clientX - rect.left) / rect.width;
      mouse.y = (e.clientY - rect.top) / rect.height;
    };

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      addRipple(
        (e.clientX - rect.left) / rect.width,
        (e.clientY - rect.top) / rect.height
      );
    };

    resize();
    drawWater();
    window.addEventListener("resize", resize);
    canvas.addEventListener("mousemove", handleMouse);
    canvas.addEventListener("click", handleClick);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", handleMouse);
      canvas.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: "auto" }}
    />
  );
}
